import { apiClient } from '@/services/api/client'
import type {
  ApiResponse,
  CreatePlatformBroadcastRequest,
  UpdatePlatformBroadcastRequest,
  EscalationListResponse,
  FeedFilters,
  FeedListResponse,
  PlatformBroadcast,
  PlatformMessage,
  PlatformMessageListResponse,
  PublishBroadcastResponse,
} from '../types'

export const feedApi = {
  /**
   * Get the current user's personal notification feed.
   */
  getMyFeed: async (filters: FeedFilters): Promise<ApiResponse<FeedListResponse>> => {
    const params = buildFeedParams(filters)
    const query = params.toString()
    const url = query
      ? `/notifications/feed/my?${query}`
      : '/notifications/feed/my'
    return apiClient.get<ApiResponse<FeedListResponse>>(url)
  },

  /**
   * Get the tenant-wide notification event feed (tenant_admin+ required).
   */
  getTenantFeed: async (filters: FeedFilters): Promise<ApiResponse<FeedListResponse>> => {
    const params = buildFeedParams(filters)
    const query = params.toString()
    const url = query
      ? `/notifications/feed/tenant?${query}`
      : '/notifications/feed/tenant'
    return apiClient.get<ApiResponse<FeedListResponse>>(url)
  },

  /**
   * Retry delivery of a failed notification feed entry.
   */
  retryDelivery: async (id: string): Promise<ApiResponse<void>> => {
    return apiClient.post<ApiResponse<void>>(`/notifications/feed/${id}/retry`, {})
  },

  /**
   * List platform-level messages (broadcasts) visible to the current tenant.
   */
  listPlatformMessages: async (
    limit?: number,
    offset?: number
  ): Promise<ApiResponse<PlatformMessageListResponse>> => {
    const params = new URLSearchParams()
    if (limit !== undefined) params.set('limit', String(limit))
    if (offset !== undefined) params.set('offset', String(offset))
    const query = params.toString()
    const url = query ? `/notifications/platform-messages?${query}` : '/notifications/platform-messages'
    return apiClient.get<ApiResponse<PlatformMessageListResponse>>(url)
  },

  /**
   * Mark a single platform message as read.
   */
  markPlatformMessageRead: async (id: string): Promise<ApiResponse<PlatformMessage>> => {
    return apiClient.post<ApiResponse<PlatformMessage>>(`/notifications/platform-messages/${id}/read`, {})
  },

  /**
   * Mark all platform messages as read for the current user.
   */
  markAllRead: async (): Promise<ApiResponse<void>> => {
    return apiClient.post<ApiResponse<void>>('/notifications/platform-messages/read-all', {})
  },

  // ─── Escalation + acknowledgement (NOTIF.P2.2) ───────────────────────────────

  /**
   * List the tenant's active (pending) notification escalations.
   */
  listEscalations: async (): Promise<ApiResponse<EscalationListResponse>> => {
    return apiClient.get<ApiResponse<EscalationListResponse>>('/notifications/escalations')
  },

  /**
   * Acknowledge the escalation for an event, stopping further escalation.
   */
  acknowledgeEscalation: async (
    eventId: string
  ): Promise<ApiResponse<{ acknowledged: boolean; event_id: string }>> => {
    return apiClient.post<ApiResponse<{ acknowledged: boolean; event_id: string }>>(
      `/notifications/escalations/${encodeURIComponent(eventId)}/ack`,
      {}
    )
  },

  // ─── Platform admin endpoints ────────────────────────────────────────────────

  /**
   * List all platform broadcasts (platform_admin required).
   */
  listBroadcasts: async (opts?: { limit?: number }): Promise<ApiResponse<PlatformBroadcast[]>> => {
    // The server returns 20 by default (max 100) and the console has no paging, so it asks for the max.
    // A list comes back as `data: [...]` with the count in `meta.total` (see useBroadcasts for the normalised shape).
    const query = opts?.limit ? `?limit=${opts.limit}` : ''
    return apiClient.get<ApiResponse<PlatformBroadcast[]>>(`/platform/broadcasts${query}`)
  },

  /**
   * One broadcast WITH its targets and their delivery state. The list omits targets, so
   * editing a draft's audience and seeing who a broadcast went to both read it from here.
   */
  getBroadcast: async (id: string): Promise<ApiResponse<PlatformBroadcast>> => {
    return apiClient.get<ApiResponse<PlatformBroadcast>>(`/platform/broadcasts/${id}`)
  },

  /**
   * Create a new platform broadcast (platform_admin required).
   */
  createBroadcast: async (
    data: CreatePlatformBroadcastRequest
  ): Promise<ApiResponse<PlatformBroadcast>> => {
    return apiClient.post<ApiResponse<PlatformBroadcast>>('/platform/broadcasts', data)
  },

  /**
   * Update an existing draft broadcast (platform_admin required).
   */
  updateBroadcast: async (
    { id, ...data }: UpdatePlatformBroadcastRequest
  ): Promise<ApiResponse<PlatformBroadcast>> => {
    return apiClient.patch<ApiResponse<PlatformBroadcast>>(`/platform/broadcasts/${id}`, data)
  },

  /**
   * Publish a draft broadcast, sending it to all targeted tenants/users.
   * H7: the response reports whether the fanout trigger that actually
   * delivers the broadcast was sent (fanout_triggered) — false (with
   * fanout_skipped_reason set) when the event bus is disabled, even though
   * the row is marked published. Callers MUST surface this.
   */
  publishBroadcast: async (id: string): Promise<ApiResponse<PublishBroadcastResponse>> => {
    return apiClient.post<ApiResponse<PublishBroadcastResponse>>(`/platform/broadcasts/${id}/publish`, {})
  },

  /**
   * Cancel a broadcast (drafts and scheduled broadcasts only).
   */
  cancelBroadcast: async (id: string): Promise<ApiResponse<void>> =>
    apiClient.post<ApiResponse<void>>(`/platform/broadcasts/${id}/cancel`, {}),

  /**
   * Delete a broadcast (only drafts and expired broadcasts may be deleted).
   */
  deleteBroadcast: async (id: string): Promise<ApiResponse<void>> => {
    return apiClient.delete<ApiResponse<void>>(`/platform/broadcasts/${id}`)
  },
}

// ─── helpers ─────────────────────────────────────────────────────────────────

// `tab` is encoded in the URL path (separate endpoints), not as a query param.
function buildFeedParams(filters: Omit<FeedFilters, 'tab'>): URLSearchParams {
  const params = new URLSearchParams()
  if (filters.channel_id) params.set('channel_id', filters.channel_id)
  if (filters.event_type) params.set('event_type', filters.event_type)
  if (filters.delivery_status) params.set('delivery_status', filters.delivery_status)
  if (filters.from) params.set('from', filters.from)
  if (filters.to) params.set('to', filters.to)
  if (filters.limit !== undefined) params.set('limit', String(filters.limit))
  if (filters.offset !== undefined) params.set('offset', String(filters.offset))
  return params
}
